"use client";

import { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, ArrowUpRight, MapPin, Star } from "lucide-react";
import { MemberOnlyCard } from "@/components/MemberOnlyCard";
import { PinkImageFallback } from "@/components/PinkImageFallback";
import type { PublicBusinessDirectoryData } from "@/lib/public-data";
import type { Business, BusinessCategory, BusinessSubcategory, Suburb } from "@/lib/supabase/types";
import { usePreferencesStore } from "@/stores/preferences-store";

function byId<T extends { id: string }>(items: T[]) {
  return items.reduce<Record<string, T | undefined>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
}

function shortDescription(business: Business, category?: BusinessCategory, subcategory?: BusinessSubcategory) {
  return business.description ?? `Discover ${subcategory?.name ?? category?.name ?? "local"} recommendations from the neighbourhood directory.`;
}

function DirectoryMiniCard({
  business,
  category,
  subcategory,
  suburb
}: {
  business: Business;
  category?: BusinessCategory;
  subcategory?: BusinessSubcategory;
  suburb?: Suburb;
}) {
  const image = business.cover_image_url ?? business.logo_url;
  const href = `/services/directory/${business.slug}`;
  return (
    <article className="group flex min-w-[245px] flex-col overflow-hidden rounded-lg border border-border bg-card shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-nv-red/35 hover:shadow-tile sm:min-w-0">
      <Link href={href} className="relative block aspect-[16/10] overflow-hidden bg-muted">
        {image ? (
          <Image
            src={image}
            alt={`${business.business_name} preview`}
            fill
            sizes="(min-width: 1280px) 250px, (min-width: 768px) 30vw, 72vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <PinkImageFallback ariaLabel={`${business.business_name} image unavailable`} headline={business.business_name} footer={category?.name ?? "Business"} className="h-full w-full" />
        )}
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#d8e85a] px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-nv-ink shadow-soft">
          {business.sponsor ? <Star className="h-3 w-3 fill-nv-ink" /> : null}
          {business.sponsor ? "Featured" : category?.name ?? "Local"}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <p className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
          <MapPin className="h-3.5 w-3.5 text-nv-red" />
          {suburb?.name ?? "Sydney"} · {subcategory?.name ?? category?.name ?? "Business"}
        </p>
        <h3 className="mt-2 line-clamp-1 font-display text-xl leading-tight tracking-tight">{business.business_name}</h3>
        <p className="mt-2 line-clamp-2 min-h-[40px] text-sm leading-5 text-muted-foreground">{shortDescription(business, category, subcategory)}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link href={href} className="inline-flex min-h-9 items-center justify-center rounded-md border border-border bg-background px-3 text-xs font-black uppercase tracking-wider transition-colors hover:border-nv-ink hover:bg-nv-ink hover:text-nv-paper">
            More info
          </Link>
          <Link href={href} className="inline-flex min-h-9 items-center justify-center rounded-md bg-nv-ink px-3 text-xs font-black uppercase tracking-wider text-nv-paper transition-colors hover:bg-nv-red">
            View
          </Link>
        </div>
      </div>
    </article>
  );
}

export function LocalRecommendations({ data }: { data: PublicBusinessDirectoryData }) {
  const selectedSuburb = usePreferencesStore((state) => state.selectedSuburb);
  const maps = useMemo(() => ({
    categories: byId(data.categories),
    subcategories: byId(data.subcategories),
    suburbs: byId(data.suburbs)
  }), [data.categories, data.subcategories, data.suburbs]);

  const suburbBusinesses = useMemo(() => {
    const selectedId = selectedSuburb?.id;
    const matching = selectedId ? data.businesses.filter((business) => business.suburb_id === selectedId) : data.businesses;
    const source = matching.length ? matching : data.businesses;
    return [...source]
      .sort((a, b) => Number(Boolean(b.sponsor)) - Number(Boolean(a.sponsor)) || a.business_name.localeCompare(b.business_name))
      .slice(0, 4);
  }, [data.businesses, selectedSuburb?.id]);

  const suburbLabel = selectedSuburb?.name ?? "Sydney";

  if (data.businesses.length === 0 && data.lockedBusinesses.length === 0) {
    return (
      <section id="featured" className="content-auto bg-nv-paper py-20 sm:py-24">
        <div className="container mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-nv-red">Local recommendations</p>
            <h2 className="mt-3 font-display text-4xl tracking-tight">Directory recommendations are coming soon.</h2>
            <p className="mx-auto mt-3 max-w-lg text-muted-foreground">Once listings are published, this section will adapt to the suburb selected in the navbar.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="featured" className="content-auto bg-[#ebe6d4] py-20 sm:py-24">
      <div className="container mx-auto">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-muted-foreground">Local picks</p>
            <h2 className="mt-2 font-display text-4xl leading-tight tracking-tight sm:text-5xl">
              Recommended in {suburbLabel}
            </h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Change your suburb in the navbar and this rail follows along with neighbourhood-specific suggestions.
            </p>
          </div>
          <Link href="/directory" className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-nv-ink transition-colors hover:text-nv-red">
            View all businesses <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-3 sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-4">
          {suburbBusinesses.map((business) => (
            <DirectoryMiniCard
              key={business.id}
              business={business}
              category={business.business_category_id ? maps.categories[business.business_category_id] : undefined}
              subcategory={business.business_subcategory_id ? maps.subcategories[business.business_subcategory_id] : undefined}
              suburb={business.suburb_id ? maps.suburbs[business.suburb_id] : undefined}
            />
          ))}
          {data.lockedBusinesses.slice(0, Math.max(0, 4 - suburbBusinesses.length)).map((teaser) => (
            <MemberOnlyCard key={teaser.id} teaser={teaser} redirectTo={`/services/directory/${teaser.slug}`} />
          ))}
        </div>
        <Link href="/directory" className="mt-6 inline-flex items-center gap-2 rounded-md bg-nv-red px-5 py-3 text-sm font-black uppercase tracking-wider text-nv-paper transition-colors hover:bg-nv-ink">
          Open NV Directory <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}
